import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { MessageSquareWarning, Wheat, Fuel, Receipt, Briefcase, GraduationCap, HeartPulse, Gift, Shield, TreePine, Radar, Megaphone, BarChart2, MapPin, TrendingUp, TrendingDown, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/Sheet';
import { useNavigate } from 'react-router-dom';
import { RelatedWorkflow } from '../components/dashboard/RelatedWorkflow';
type Theme = {
  icon: any;
  name: string;
  vol: string;
  neg: number;
  top: string;
  color: string;
  regions: {
    name: string;
    share: number;
  }[];
  posts: {
    handle: string;
    platform: string;
    text: string;
    reach: string;
  }[];
  pic: string[];
  trend: 'up' | 'down' | 'flat';
  trendPct: number;
  insight: string;
};
const themes: Theme[] = [{
  icon: Wheat,
  name: 'Pangan',
  vol: '142K',
  neg: 72,
  top: 'Harga beras, kelangkaan pupuk, distribusi MBG',
  color: 'text-red-500',
  trend: 'up',
  trendPct: 18,
  regions: [{
    name: 'Jawa Barat',
    share: 28
  }, {
    name: 'Jawa Tengah',
    share: 22
  }, {
    name: 'Sumatera Utara',
    share: 14
  }, {
    name: 'Sulawesi Selatan',
    share: 10
  }],
  posts: [{
    handle: '@tanisejahtera_id',
    platform: 'X',
    text: 'Pupuk subsidi mulai sulit di gudang kami, Kementan tolong koordinasi distributor.',
    reach: '24K'
  }, {
    handle: '@warga_demak',
    platform: 'TikTok',
    text: 'Harga beras di pasar tradisional naik 18% minggu ini. Bansos belum sampai.',
    reach: '142K'
  }],
  pic: ['KEMENTAN', 'BAPANAS', 'BULOG', 'KEMDAG'],
  insight: 'Eskalasi keluhan +18% dalam 24 jam. Risiko mobilisasi petani di Jabar & Jateng jika data stok BULOG tidak dirilis dalam 8 jam.'
}, {
  icon: Fuel,
  name: 'BBM',
  vol: '89K',
  neg: 65,
  top: 'Subsidi tidak tepat sasaran, antrean SPBU',
  color: 'text-orange-500',
  trend: 'up',
  trendPct: 12,
  regions: [{
    name: 'Jakarta',
    share: 24
  }, {
    name: 'Jawa Timur',
    share: 19
  }, {
    name: 'Kalimantan Timur',
    share: 15
  }, {
    name: 'Riau',
    share: 12
  }],
  posts: [{
    handle: '@suara_pengemudi',
    platform: 'X',
    text: 'Antrean MyPertamina di SPBU Cawang 3 jam. Sistem sering error.',
    reach: '38K'
  }],
  pic: ['ESDM', 'PERTAMINA', 'BPH MIGAS'],
  insight: 'Antrean panjang & error sistem MyPertamina jadi pemicu utama. Pertimbangkan rilis status sistem realtime.'
}, {
  icon: Receipt,
  name: 'Pajak',
  vol: '62K',
  neg: 78,
  top: 'Pajak hiburan 75%, PPN 12%, prosedur SPT',
  color: 'text-red-500',
  trend: 'up',
  trendPct: 24,
  regions: [{
    name: 'Bali',
    share: 32
  }, {
    name: 'Jakarta',
    share: 21
  }, {
    name: 'DI Yogyakarta',
    share: 14
  }],
  posts: [{
    handle: '@kritik_pedas99',
    platform: 'X',
    text: 'Pajak hiburan 75% mematikan industri di Bali. Banyak teman PHK.',
    reach: '120K'
  }],
  pic: ['KEMENKEU', 'DJP', 'KEMENPAR'],
  insight: 'Sentimen pajak hiburan didominasi pelaku usaha Bali. Risiko kampanye #StopPajakHiburan jika tidak ada moderasi tarif.'
}, {
  icon: Briefcase,
  name: 'Pekerjaan',
  vol: '78K',
  neg: 60,
  top: 'UMP, PHK industri, lapangan kerja muda',
  color: 'text-amber-500',
  trend: 'up',
  trendPct: 8,
  regions: [{
    name: 'Jawa Barat',
    share: 26
  }, {
    name: 'Banten',
    share: 18
  }, {
    name: 'Jawa Tengah',
    share: 14
  }],
  posts: [{
    handle: '@buruh_bergerak',
    platform: 'X',
    text: 'UMP 2026 tidak adil. Inflasi lebih tinggi dari kenaikan upah.',
    reach: '95K'
  }],
  pic: ['KEMNAKER', 'KEMENKO PEREKONOMIAN'],
  insight: 'Demo buruh terkoordinasi di 3 provinsi. Statement Menaker hilang dari ruang publik — segera adakan dialog tripartit.'
}, {
  icon: GraduationCap,
  name: 'Pendidikan',
  vol: '34K',
  neg: 45,
  top: 'UKT mahal, akses sekolah daerah, kurikulum baru',
  color: 'text-blue-500',
  trend: 'flat',
  trendPct: 2,
  regions: [{
    name: 'Jakarta',
    share: 22
  }, {
    name: 'Jawa Barat',
    share: 18
  }, {
    name: 'Papua',
    share: 16
  }],
  posts: [{
    handle: '@mahasiswa_id',
    platform: 'IG',
    text: 'UKT naik tanpa pemberitahuan jelas. Mahasiswa baru bingung.',
    reach: '18K'
  }],
  pic: ['KEMENDIKBUDRISTEK', 'KEMENAG'],
  insight: 'Keluhan stabil. Fokus advokasi pada akses sekolah 3T dan komunikasi UKT.'
}, {
  icon: HeartPulse,
  name: 'Kesehatan',
  vol: '41K',
  neg: 52,
  top: 'BPJS, antrean RS, harga obat',
  color: 'text-amber-500',
  trend: 'down',
  trendPct: 4,
  regions: [{
    name: 'Jakarta',
    share: 24
  }, {
    name: 'Jawa Barat',
    share: 16
  }, {
    name: 'Papua',
    share: 14
  }],
  posts: [{
    handle: '@pasien_bpjs',
    platform: 'FB',
    text: 'Antrean BPJS di RS daerah masih sangat panjang. Obat sering habis.',
    reach: '12K'
  }],
  pic: ['KEMENKES', 'BPJS KESEHATAN'],
  insight: 'Trending menurun, namun isu kekurangan dokter di Papua perlu eskalasi terpisah.'
}, {
  icon: Gift,
  name: 'Bansos',
  vol: '98K',
  neg: 70,
  top: 'Distribusi tidak merata, hoaks penarikan',
  color: 'text-red-500',
  trend: 'up',
  trendPct: 32,
  regions: [{
    name: 'Jawa Tengah',
    share: 28
  }, {
    name: 'Jawa Timur',
    share: 22
  }, {
    name: 'NTT',
    share: 14
  }],
  posts: [{
    handle: '@infodaerah_up',
    platform: 'IG',
    text: 'INFO PENTING: bansos pangan dikabarkan dihentikan? Cek sumber resmi.',
    reach: '210K'
  }],
  pic: ['KEMENSOS', 'KOMDIGI', 'KSP'],
  insight: 'Hoaks "penarikan bansos" viral. Komdigi & KSP wajib rilis klarifikasi infografis < 4 jam.'
}, {
  icon: Shield,
  name: 'Aparat',
  vol: '52K',
  neg: 68,
  top: 'Pungli, kekerasan oknum, profesionalisme',
  color: 'text-amber-500',
  trend: 'up',
  trendPct: 6,
  regions: [{
    name: 'Sumatera Utara',
    share: 22
  }, {
    name: 'Papua',
    share: 20
  }, {
    name: 'Jakarta',
    share: 14
  }],
  posts: [{
    handle: '@warga_medan',
    platform: 'TikTok',
    text: 'Video pungli di jalan tol viral. Polri harus tindak tegas.',
    reach: '84K'
  }],
  pic: ['POLRI', 'KOMPOLNAS', 'PROPAM'],
  insight: 'Isu pungli & doxxing aparat di Papua butuh respons Polri terkoordinasi.'
}, {
  icon: TreePine,
  name: 'Lingkungan',
  vol: '38K',
  neg: 55,
  top: 'Deforestasi, polusi udara, banjir Demak',
  color: 'text-amber-500',
  trend: 'down',
  trendPct: 3,
  regions: [{
    name: 'Jawa Tengah',
    share: 24
  }, {
    name: 'Kalimantan Timur',
    share: 18
  }, {
    name: 'Riau',
    share: 14
  }],
  posts: [{
    handle: '@warga_demak',
    platform: 'TikTok',
    text: 'Banjir Demak hari ke-12. Bantuan dari pusat sudah datang.',
    reach: '64K'
  }],
  pic: ['KLHK', 'BNPB', 'KEMENPUPR'],
  insight: 'Bantuan banjir Demak diapresiasi. Pertahankan transparansi distribusi.'
}];
export function PublicGrievance() {
  const nav = useNavigate();
  const [selected, setSelected] = useState<Theme | null>(null);
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-2994">
      <PageHeader group="Radar Isu" title="Public Grievance Radar" description="Pemantauan tematik keluhan rakyat. Sumber: kanal aduan resmi, media sosial publik, dan media massa." icon={MessageSquareWarning} data-id="element-2995" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2996">
        {[{
        label: 'Total Keluhan 24J',
        value: '634K'
      }, {
        label: 'Tema Aktif',
        value: '9'
      }, {
        label: 'Wilayah Terdampak',
        value: '34'
      }].map(k => <Card key={k.label} className="bg-card border-border/50" data-id="element-2997">
            <CardContent className="p-4" data-id="element-2998">
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-2999">
                {k.label}
              </p>
              <p className="font-heading text-2xl font-bold mt-1" data-id="element-3000">{k.value}</p>
            </CardContent>
          </Card>)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3001">
        {themes.map(t => <Card key={t.name} className="bg-card border-border/50 hover:bg-muted/30 hover:border-primary/30 transition cursor-pointer group" onClick={() => setSelected(t)} data-id="element-3002">
            <CardContent className="p-4" data-id="element-3003">
              <div className="flex items-center justify-between mb-3" data-id="element-3004">
                <div className="flex items-center gap-2" data-id="element-3005">
                  <div className="size-9 rounded bg-muted/30 border border-border/50 flex items-center justify-center" data-id="element-3006">
                    <t.icon className={`size-4 ${t.color}`} data-id="element-3007" />
                  </div>
                  <h3 className="font-bold text-base" data-id="element-3008">{t.name}</h3>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-3009">
                  <span className="font-mono text-xs text-muted-foreground" data-id="element-3010">
                    {t.vol}
                  </span>
                  {t.trend === 'up' ? <TrendingUp className="size-3 text-red-500" data-id="element-3011" /> : t.trend === 'down' ? <TrendingDown className="size-3 text-emerald-500" data-id="element-3012" /> : null}
                </div>
              </div>

              <div className="mb-3" data-id="element-3013">
                <div className="flex justify-between font-mono text-[9px] text-muted-foreground mb-1" data-id="element-3014">
                  <span data-id="element-3015">NEGATIF</span>
                  <span data-id="element-3016">{t.neg}%</span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-3017">
                  <div className={`h-full ${t.neg >= 70 ? 'bg-red-500' : t.neg >= 50 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{
                width: `${t.neg}%`
              }} data-id="element-3018" />
                </div>
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2" data-id="element-3019">
                {t.top}
              </p>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/40" data-id="element-3020">
                <span className="font-mono text-[9px] text-muted-foreground" data-id="element-3021">
                  {t.pic.length} K/L terlibat
                </span>
                <span className="font-mono text-[9px] text-primary opacity-0 group-hover:opacity-100 transition flex items-center gap-1" data-id="element-3022">
                  Detail <ArrowRight className="size-3" data-id="element-3023" />
                </span>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={o => !o && setSelected(null)} data-id="element-3024">
        <SheetContent className="w-full sm:max-w-xl bg-background border-border/60 p-0 flex flex-col" data-id="element-3025">
          {selected && <>
              <SheetHeader className="px-6 py-5 border-b border-border/60" data-id="element-3026">
                <div className="flex items-center gap-3 mb-2" data-id="element-3027">
                  <div className="size-10 rounded-md bg-muted/30 border border-border/50 flex items-center justify-center" data-id="element-3028">
                    <selected.icon className={`size-5 ${selected.color}`} data-id="element-3029" />
                  </div>
                  <div data-id="element-3030">
                    <SheetTitle className="text-lg leading-tight" data-id="element-3031">
                      Tema: {selected.name}
                    </SheetTitle>
                    <SheetDescription data-id="element-3032">
                      Volume 24 jam: {selected.vol} · Negatif: {selected.neg}%
                    </SheetDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-3033">
                  <Badge variant="outline" className={`font-mono text-[10px] h-5 ${selected.trend === 'up' ? 'bg-red-50 text-red-700 border-red-200' : selected.trend === 'down' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`} data-id="element-3034">
                    {selected.trend === 'up' ? <TrendingUp className="size-3 mr-1" data-id="element-3035" /> : selected.trend === 'down' ? <TrendingDown className="size-3 mr-1" data-id="element-3036" /> : null}
                    {selected.trend === 'flat' ? 'Stabil' : `${selected.trendPct}% vs minggu lalu`}
                  </Badge>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-id="element-3037">
                {/* Top complaint */}
                <div data-id="element-3038">
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-3039">
                    Topik Dominan
                  </p>
                  <p className="text-sm leading-relaxed" data-id="element-3040">{selected.top}</p>
                </div>

                {/* Region breakdown */}
                <div data-id="element-3041">
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-3042">
                    Sebaran Wilayah
                  </p>
                  <div className="space-y-2" data-id="element-3043">
                    {selected.regions.map(r => <div key={r.name} className="flex items-center gap-3" data-id="element-3044">
                        <MapPin className="size-3 text-muted-foreground shrink-0" data-id="element-3045" />
                        <span className="text-xs font-medium w-32 shrink-0 truncate" data-id="element-3046">
                          {r.name}
                        </span>
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-3047">
                          <div className={`h-full ${selected.color.replace('text-', 'bg-')}`} style={{
                      width: `${r.share * 3}%`
                    }} data-id="element-3048" />
                        </div>
                        <span className="font-mono text-[10px] text-muted-foreground w-8 text-right" data-id="element-3049">
                          {r.share}%
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Top posts */}
                <div data-id="element-3050">
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-3051">
                    Postingan Berdampak
                  </p>
                  <div className="space-y-2" data-id="element-3052">
                    {selected.posts.map((p, i) => <div key={i} className="p-3 rounded-lg border border-border/50 bg-muted/10" data-id="element-3053">
                        <div className="flex items-center justify-between mb-1.5" data-id="element-3054">
                          <span className="font-mono text-[10px] font-bold" data-id="element-3055">
                            {p.handle}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-3056">
                            <span className="font-mono text-[9px] bg-muted px-1.5 py-0.5 rounded" data-id="element-3057">
                              {p.platform}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground" data-id="element-3058">
                              {p.reach}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-foreground/80 leading-relaxed" data-id="element-3059">
                          {p.text}
                        </p>
                      </div>)}
                  </div>
                </div>

                {/* PIC */}
                <div data-id="element-3060">
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-3061">
                    K/L Berwenang
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-3062">
                    {selected.pic.map(p => <Badge key={p} variant="outline" className="font-mono text-[10px] h-5 bg-primary/5 border-primary/20 text-primary" data-id="element-3063">
                        {p}
                      </Badge>)}
                  </div>
                </div>

                {/* AI insight */}
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-id="element-3064">
                  <p className="font-mono text-[10px] uppercase text-primary font-bold flex items-center gap-1.5" data-id="element-3065">
                    <Sparkles className="size-3" data-id="element-3066" /> AI Analyst Insight
                  </p>
                  <p className="text-xs text-foreground/80 mt-1.5 leading-relaxed" data-id="element-3067">
                    {selected.insight}
                  </p>
                </div>
              </div>

              <div className="border-t border-border/60 p-4 flex gap-2 bg-card/50" data-id="element-3068">
                <Button className="flex-1" onClick={() => {
              nav('/command-tasking');
              setSelected(null);
              toast.success(`Tasking ${selected.name} dibuka`);
            }} data-id="element-3069">
                  <Briefcase className="size-3.5 mr-1.5" data-id="element-3070" />
                  Buat Tasking K/L
                </Button>
                <Button variant="outline" onClick={() => {
              nav('/response-recommender');
              setSelected(null);
            }} data-id="element-3071">
                  <Megaphone className="size-3.5 mr-1.5" data-id="element-3072" />
                  Generate Respons
                </Button>
              </div>
            </>}
        </SheetContent>
      </Sheet>

      <RelatedWorkflow currentStep="Radar Keluhan Publik" nextSteps={[{
      to: '/hot-issue',
      icon: Radar,
      label: 'Cek Hot Issue',
      desc: 'Lihat isu terkait yang sedang trending.'
    }, {
      to: '/response-recommender',
      icon: Megaphone,
      label: 'Generate Respons',
      desc: 'Siapkan tanggapan resmi multi-format.'
    }, {
      to: '/command-tasking',
      icon: Briefcase,
      label: 'Tasking K/L',
      desc: 'Delegasikan ke kementerian terkait keluhan.'
    }, {
      to: '/ipoleksosbudhankam',
      icon: BarChart2,
      label: 'Cek Stabilitas',
      desc: 'Konteks ke pilar IPOLEKSOSBUDHANKAM nasional.'
    }]} data-id="element-3073" />
    </div>;
}